// File: src/components/Sidebar.js
/*
 * =====================================================================
 * WHAT CHANGED vs ORIGINAL:
 * -----------------------------------------------------------------------
 * ORIGINAL:
 *   - ADMIN_NAV had MANAGER, HR, OWNER sections only
 *   - No APPROVAL_MANAGER nav items
 *   - Sidebar role logic: super_admin / owner / hr / manager / employee
 *
 * NEW:
 *   - Added APPROVAL_MANAGER section to ADMIN_NAV with:
 *     • My Dashboard (employee ESS)
 *     • All ESS nav items (attendance, leave, etc.) — they are also employees
 *     • Approval Manager Dashboard (new page)
 *     • Leave Approvals
 *     • Attendance Approvals
 *     • Missed Punch Approvals
 *     • Sudden Request Approvals
 *   - Sidebar role logic now includes ROLE_APPROVAL_MANAGER branch
 *   - RoleChip label shows "APPROVAL MGR" for approval managers
 *   - Group header "Approvals" shown for approval manager admin section
 * =====================================================================
 */
import React from 'react';
import {
  Dashboard,
  CalendarMonth,
  FilePresent,
  AttachMoney,
  Person,
  AccountBalance,
  Assessment,
  AccessTimeFilled,
  Key,
  Settings,
  Paid,
  AdminPanelSettings,
  FlashOn,
  EventNote,
  Lock,
  AssignmentTurnedIn,
  RequestQuote,
  Payments,
  HowToReg,
} from '@mui/icons-material';

import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Avatar
} from '@mui/material';

import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* ================= EMPLOYEE (ESS) ================= */
const ESS_NAV = [
  { title: 'My Dashboard',             path: '/employee/dashboard',       icon: <Dashboard /> },
  { title: 'Onboarding Form',           path: '/employee/onboarding',      icon: <AssignmentTurnedIn /> },
  { title: 'My Profile',                path: '/employee/profile',         icon: <Person /> },
  { title: 'Attendance',                path: '/employee/attendance',       icon: <AccessTimeFilled /> },
  { title: 'My Daily Pay',              path: '/employee/daily-pay',       icon: <Paid /> },
  { title: 'Attendance Calendar',       path: '/employee/calendar',        icon: <EventNote /> },
  { title: 'Leave Request',             path: '/employee/leave-request',   icon: <CalendarMonth /> },
  { title: 'Sudden Request',            path: '/employee/sudden-request',  icon: <FlashOn /> },
  { title: 'Attendance Regularization', path: '/employee/missed-punch',    icon: <EventNote /> },
  { title: 'Bank Details',              path: '/employee/bank-details',    icon: <AccountBalance /> },
  { title: 'My Documents',             path: '/employee/document-upload', icon: <FilePresent /> },
  { title: 'My Payslips',              path: '/employee/payslips',        icon: <AttachMoney /> },
  { title: 'Company Holidays',         path: '/employee/holidays',        icon: <CalendarMonth /> },
];

/* ================= ADMIN NAV ================= */
const ADMIN_NAV = {

  // NEW: APPROVAL_MANAGER nav — first-level approver
  APPROVAL_MANAGER: [
    /* ESS section — approval managers are employees too */
    { title: 'My Dashboard',              path: '/employee/dashboard',       icon: <Dashboard />,         group: 'employee' },
    { title: 'Onboarding Form',           path: '/employee/onboarding',      icon: <AssignmentTurnedIn />, group: 'employee' },
    { title: 'My Profile',                path: '/employee/profile',         icon: <Person />,            group: 'employee' },
    { title: 'Attendance',                path: '/employee/attendance',       icon: <AccessTimeFilled />, group: 'employee' },
    { title: 'My Daily Pay',              path: '/employee/daily-pay',       icon: <Paid />,              group: 'employee' },
    { title: 'Attendance Calendar',       path: '/employee/calendar',        icon: <EventNote />,         group: 'employee' },
    { title: 'Leave Request',             path: '/employee/leave-request',   icon: <CalendarMonth />,    group: 'employee' },
    { title: 'Sudden Request',            path: '/employee/sudden-request',  icon: <FlashOn />,           group: 'employee' },
    { title: 'Attendance Regularization',path: '/employee/missed-punch',    icon: <EventNote />,         group: 'employee' },
    { title: 'Bank Details',              path: '/employee/bank-details',    icon: <AccountBalance />,    group: 'employee' },
    { title: 'My Documents',             path: '/employee/document-upload', icon: <FilePresent />,      group: 'employee' },
    { title: 'My Payslips',              path: '/employee/payslips',        icon: <AttachMoney />,      group: 'employee' },
    { title: 'Company Holidays',         path: '/employee/holidays',        icon: <CalendarMonth />,    group: 'employee' },

    /* Approval Manager admin section */
    { title: 'AM Dashboard',             path: '/approval-manager/dashboard',         icon: <Dashboard /> },
    { title: 'Leave Approvals',          path: '/approval-manager/leave-approvals',   icon: <CalendarMonth /> },
    { title: 'Attendance Approvals',     path: '/approval-manager/attendance-approvals', icon: <AccessTimeFilled /> },
    { title: 'Missed Punch Approvals',   path: '/approval-manager/missed-punch',      icon: <EventNote /> },
    { title: 'Sudden Approvals',         path: '/approval-manager/sudden-approvals',  icon: <FlashOn /> },
  ],

  MANAGER: [
    { title: 'Approval Center',                          path: '/hr/approvals',                    icon: <Lock /> },
    { title: 'Sudden Approvals',                         path: '/hr/sudden-approvals',              icon: <FlashOn /> },
    { title: 'Attendance Regularization Approvals',      path: '/manager/missed-punch-approvals',  icon: <EventNote /> },
  ],

  HR: [
    /* ESS section */
    { title: 'My Dashboard',              path: '/employee/dashboard',       icon: <Dashboard />,         group: 'employee' },
    { title: 'Onboarding Form',           path: '/employee/onboarding',      icon: <AssignmentTurnedIn />, group: 'employee' },
    { title: 'My Profile',                path: '/employee/profile',         icon: <Person />,            group: 'employee' },
    { title: 'Attendance',                path: '/employee/attendance',       icon: <AccessTimeFilled />, group: 'employee' },
    { title: 'My Daily Pay',              path: '/employee/daily-pay',       icon: <Paid />,              group: 'employee' },
    { title: 'Attendance Calendar',       path: '/employee/calendar',        icon: <EventNote />,         group: 'employee' },
    { title: 'Leave Request',             path: '/employee/leave-request',   icon: <CalendarMonth />,    group: 'employee' },
    { title: 'Sudden Request',            path: '/employee/sudden-request',  icon: <FlashOn />,           group: 'employee' },
    { title: 'Attendance Regularization',path: '/employee/missed-punch',    icon: <EventNote />,         group: 'employee' },
    { title: 'Bank Details',              path: '/employee/bank-details',    icon: <AccountBalance />,    group: 'employee' },
    { title: 'My Documents',             path: '/employee/document-upload', icon: <FilePresent />,      group: 'employee' },
    { title: 'My Payslips',              path: '/employee/payslips',        icon: <AttachMoney />,      group: 'employee' },
    { title: 'Company Holidays',         path: '/employee/holidays',        icon: <CalendarMonth />,    group: 'employee' },

    /* HR admin section */
    { title: 'HR Dashboard',             path: '/hr/dashboard',             icon: <Dashboard /> },
    { title: 'Onboarding Approvals',     path: '/hr/onboarding-approvals',  icon: <AssignmentTurnedIn /> },
    { title: 'Document Approvals',       path: '/hr/document-approvals',    icon: <FilePresent /> },
    { title: 'Employee Management',      path: '/hr/employee-management',   icon: <Person /> },
    { title: 'Branch Employees',         path: '/hr/branch-employees',      icon: <Person /> },
    { title: 'Incentives',               path: '/hr/incentives',            icon: <Payments /> },
    { title: 'Other Allowances',         path: '/hr/other-allowances',      icon: <RequestQuote /> },
    { title: 'Advance Salary',           path: '/hr/advance-salary',        icon: <Paid /> },
    { title: 'Payroll & Docs',           path: '/hr/payroll-docs',          icon: <AttachMoney /> },
    { title: 'HR Reports',               path: '/hr/reports',               icon: <Assessment /> },
    { title: 'Approval Center',          path: '/hr/approvals',             icon: <Lock /> },
    { title: 'Sudden Approvals',         path: '/hr/sudden-approvals',      icon: <FlashOn /> },
    { title: 'Company Calendar',         path: '/hr/calendar',              icon: <CalendarMonth /> },
    { title: 'Company Settings',         path: '/company-settings',         icon: <Settings /> },
    { title: 'Bank Verification',        path: '/hr/bank-verification',     icon: <AdminPanelSettings /> },
    { title: 'Manual Attendance',        path: '/hr/manual-attendance',     icon: <EventNote /> },
  ],

  OWNER: [
    { title: 'Owner Dashboard',                      path: '/owner/dashboard',             icon: <Dashboard /> },
    { title: 'Reports',                              path: '/hr/reports',                  icon: <Assessment /> },
    { title: 'Company Policy',                       path: '/admin/policy',                icon: <Settings /> },
    { title: 'Incentives',                           path: '/hr/incentives',               icon: <Payments /> },
    { title: 'Other Allowances',                     path: '/hr/other-allowances',         icon: <RequestQuote /> },
    { title: 'Advance Salary',                       path: '/hr/advance-salary',           icon: <Paid /> },
    { title: 'Approval Center',                      path: '/hr/approvals',                icon: <Lock /> },
    { title: 'Sudden Approvals',                     path: '/hr/sudden-approvals',         icon: <FlashOn /> },
    { title: 'Attendance Regularization Audit',       path: '/owner/missed-punch-audit',    icon: <EventNote /> },
    { title: 'Manual Excel Upload',                  path: '/hr/manual-attendance',        icon: <EventNote /> },
    { title: 'Employee Management',                  path: '/hr/employee-management',      icon: <Person /> },
    { title: 'Bank Verification',                    path: '/hr/bank-verification',        icon: <AdminPanelSettings /> },
    { title: 'Company Settings',                     path: '/company-settings',            icon: <Settings /> },
  ],
};

const SUPER_ADMIN_NAV = [
  { title: 'Super Admin Dashboard', path: '/superadmin/dashboard', icon: <Dashboard /> },
  { title: 'Plan Management',       path: '/superadmin/plans',     icon: <Paid /> },
  { title: 'Assign Subscription',   path: '/superadmin/assign',    icon: <AssignmentTurnedIn /> },
  { title: 'Companies',             path: '/superadmin/companies', icon: <AdminPanelSettings /> },
];

/* ================= ROLE CHIP ================= */
const RoleChip = ({ label }) => (
  <Box
    sx={{
      mt: 0.5,
      px: 1.5,
      py: 0.4,
      borderRadius: 2,
      backgroundColor: '#dcfce7',
      color: '#15803d',
      fontSize: 11,
      fontWeight: 800,
      display: 'inline-block',
    }}
  >
    {label}
  </Box>
);

/* ================= SIDEBAR ================= */
const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return null;

  const roles = user.roles || [];
  let nav = [];
  let label = '';

  if (roles.includes('ROLE_SUPER_ADMIN')) {
    nav = [...SUPER_ADMIN_NAV];
    label = 'SUPER ADMIN';
  } else if (roles.includes('ROLE_OWNER')) {
    nav = [...ADMIN_NAV.OWNER];
    label = 'OWNER';
  } else if (roles.includes('ROLE_HR')) {
    nav = [...ADMIN_NAV.HR];
    label = 'HR';
  } else if (roles.includes('ROLE_MANAGER')) {
    nav = [...ESS_NAV, ...ADMIN_NAV.MANAGER];
    label = 'MANAGER';
  } else if (roles.includes('ROLE_APPROVAL_MANAGER')) {
    // NEW: Approval manager gets their own nav
    nav = [...ADMIN_NAV.APPROVAL_MANAGER];
    label = 'APPROVAL MGR';
  } else {
    nav = [...ESS_NAV];
    label = 'EMPLOYEE';
  }

  const uniqueNav = [];
  const seen = new Set();
  nav.forEach(i => {
    if (!seen.has(i.title)) {
      seen.add(i.title);
      uniqueNav.push(i);
    }
  });

  // Show section headers for HR and APPROVAL_MANAGER (both have employee + admin sections)
  const isHR = roles.includes('ROLE_HR') && !roles.includes('ROLE_OWNER');
  const isAM = roles.includes('ROLE_APPROVAL_MANAGER');

  let lastGroup = null;
  const renderList = [];

  uniqueNav.forEach((item) => {
    const grp = item.group || 'admin';

    if ((isHR || isAM) && grp !== lastGroup) {
      if (grp === 'employee') {
        renderList.push({ type: 'header', label: 'My Self', key: 'h-employee' });
      } else if (lastGroup === 'employee') {
        // NEW: Different header label for approval manager
        renderList.push({
          type: 'header',
          label: isAM ? 'Approvals' : 'HR Admin',
          key: 'h-admin'
        });
      }
      lastGroup = grp;
    }
    renderList.push({ type: 'item', item });
  });

  return (
    <Box
      sx={{
        width: 260,
        height: '100vh',
        backgroundColor: '#c2e6f1eb',
        borderRight: '1px solid #9ef3f8ff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, backgroundColor: '#d1f0f780' }}>
        <Typography variant="h6" fontWeight={800} color="#2563eb">
          DD-HRMS
        </Typography>
      </Box>

      <Box sx={{ p: 2, textAlign: 'center', flexShrink: 0 }}>
        <Avatar sx={{ bgcolor: '#0F172A', mx: 'auto', mb: 1, width: 52, height: 52, fontWeight: 800 }}>
          {user.username?.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="body2" fontWeight={700} color="#0F172A" noWrap sx={{ px: 1 }}>
          {user.username}
        </Typography>
        <RoleChip label={label} />
      </Box>

      <Divider />

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 0,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#16a34a40', borderRadius: 4 }
        }}
      >
        <List sx={{ px: 1, py: 1 }}>
          {renderList.map((entry, index) => {
            if (entry.type === 'header') {
              return (
                <Box key={entry.key} sx={{ px: 1.5, pt: 2, pb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, color: '#0F172A99', letterSpacing: 1, textTransform: 'uppercase' }}>
                    {entry.label}
                  </Typography>
                </Box>
              );
            }

            const { item } = entry;
            const active = location.pathname === item.path;

            return (
              <ListItem key={item.title || index} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: active ? '#bbf7d0' : 'transparent',
                    '&:hover': { backgroundColor: '#dcfce7' },
                  }}
                >
                  <ListItemIcon sx={{ color: active ? '#15803d' : '#16a34a', minWidth: 36 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 700 : 500, color: '#065f46' }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider />

      <Box sx={{ p: 1, flexShrink: 0 }}>
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/profile/change-password" sx={{ borderRadius: 2 }}>
              <ListItemIcon sx={{ color: '#ca8a04', minWidth: 36 }}><Key /></ListItemIcon>
              <ListItemText primary="Security Settings" primaryTypographyProps={{ fontSize: 14, color: '#065f46', fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/profile/settings" sx={{ borderRadius: 2 }}>
              <ListItemIcon sx={{ color: '#0284c7', minWidth: 36 }}><Settings /></ListItemIcon>
              <ListItemText primary="App Config" primaryTypographyProps={{ fontSize: 14, color: '#065f46', fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );
};

export default Sidebar;